import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { unionBy, remove } from 'lodash'
import store from '@/store'

// Helper function
const getBlankPage = () => {
  return {
    id: null,
    issue: { id: null, title: '' },
    title: '',
    slug: '',
    notes: '',
    story_count: 0,
    list_order: 1,
    _destroy: false
  }
}

export const usePagesStore = defineStore('pages', {
  state: () => ({
    edit: false,
    pages: [],
    fetchInitiatedIssues: []
  }),

  getters: {
    forIssue: (state) => ({ id }) => {
      return state.pages.filter(page => page.issue.id === id)
    },

    forId: (state) => (id) => {
      return state.pages.find(page => page.id === id)
    },

    fetchInitiatedFor: (state) => ({ issueId }) => {
      return state.fetchInitiatedIssues.includes(Number(issueId))
    }
  },

  actions: {
    addIssueToFetchInitiated({ issueId }) {
      const id = Number(issueId)
      if (!this.fetchInitiatedIssues.includes(id)) {
        this.fetchInitiatedIssues.push(id)
      }
    },

    add(page) {
      this.pages.push({ ...getBlankPage(), ...page })
    },

    setPages(pages) {
      this.pages = pages.map(page => ({
        ...page,
        _destroy: false
      }))
    },

    removeIssue({ issueId }) {
      remove(this.pages, (page) => page.issue.id === issueId)
    },

    async mergePages(pages) {
      this.setPages(unionBy(pages.concat(this.pages), 'id'))
    },

    async fetch({ issueId }) {
      this.addIssueToFetchInitiated({ issueId })

      return httpClient.get(`/api/internal/issues/${issueId}/pages`)
        .then(({ data: { pages }}) => {
          this.mergePages(pages)
        })
        .catch(err => {
          console.error(err)
          store.dispatch('messages/addError', err)
        })
    },

    async fetchOnce({ issueId }) {
      if (this.fetchInitiatedFor({ issueId })) return Promise.resolve()
      return this.fetch({ issueId })
    },

    async save({ issueId }) {
      const issuePages = this.forIssue({ id: Number(issueId) })

      return new Promise((resolve, reject) => {
        httpClient.patch(`/api/internal/issues/${issueId}`, {
          issue: { pages_attributes: issuePages }
        })
        .then((response) => {
          // Clear out all of the objects for the issue before we merge in the
          // new ones. Because the current ones have null ids which means ids
          // won't match and we'll get duplicates.
          this.removeIssue({ issueId })
          this.mergePages(response.data.issue.pages)
          resolve(response)
        })
        .catch((data) => {
          reject(data)
        })
      })
    }
  }
})
